import { useEffect, useState } from "react";
import { VideoCamera, CircleNotch, Play } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

const STATUS_STYLE = {
  draft: "bg-stone-200 text-stone-700",
  approved: "bg-[#2D5A27]/15 text-[#2D5A27]",
  rendering: "bg-[#B7791F]/15 text-[#8a5d14]",
  ready: "bg-[#27AE60]/15 text-[#1e8449]",
  error: "bg-[#C0392B]/15 text-[#C0392B]",
};

export default function TaskVideosCard() {
  const [videos, setVideos] = useState(null);
  const [avatars, setAvatars] = useState([]);
  const [voices, setVoices] = useState([]);
  const [edits, setEdits] = useState({});
  const [pick, setPick] = useState({});
  const [busy, setBusy] = useState("");

  const load = () => api.get("/task-videos").then((r) => setVideos(r.data.videos || [])).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => {
    load();
    api.get("/training-creator/heygen/avatars").then((r) => setAvatars(r.data.avatars || [])).catch(() => {});
    api.get("/training-creator/heygen/voices").then((r) => setVoices(r.data.voices || [])).catch(() => {});
  }, []);

  const generate = async () => {
    setBusy("gen");
    try {
      const { data } = await api.post("/task-videos/generate-scripts");
      toast.success(data.created.length ? `Drafted ${data.created.length} script(s)` : "All core codes already have scripts");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const saveScript = async (v) => {
    setBusy(v.id);
    try {
      await api.put(`/task-videos/${v.id}`, { script: edits[v.id] ?? v.script });
      toast.success("Script saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const approve = async (v) => {
    setBusy(v.id);
    try {
      if ((edits[v.id] ?? v.script) !== v.script) await api.put(`/task-videos/${v.id}`, { script: edits[v.id] });
      await api.put(`/task-videos/${v.id}`, { status: "approved" });
      toast.success(`${v.label} approved — ready to render`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  const render = async (v) => {
    const sel = pick[v.id] || {};
    if (!sel.avatar) { toast.error("Pick an avatar first"); return; }
    setBusy(v.id);
    try {
      await api.post(`/task-videos/${v.id}/render`, { avatar_id: sel.avatar, voice_id: sel.voice || "" });
      toast.success(`Rendering ${v.label} — this takes several minutes`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div className="mb-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="task-videos-card">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <VideoCamera size={18} className="text-primary" />
          <div>
            <p className="font-heading text-sm font-semibold text-stone-900">Task-code training videos</p>
            <p className="text-xs text-stone-400">AI-drafted scripts from your guidelines — you review and approve each, pick the avatar, then render. Ready videos appear at the top of that task's guidelines for staff.</p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={generate} disabled={busy === "gen"} className="border-stone-300" data-testid="task-videos-generate">
          {busy === "gen" ? <CircleNotch size={14} className="mr-1 animate-spin" /> : null} Generate scripts (AI)
        </Button>
      </div>

      {videos && videos.length > 0 && (
        <div className="mt-4 space-y-3">
          {videos.map((v) => (
            <div key={v.id} className="rounded-lg border border-stone-200 p-4" data-testid={`task-video-${v.task_code}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="text-sm font-semibold text-stone-900">{v.task_code} — {v.label}</p>
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_STYLE[v.status] || STATUS_STYLE.draft}`} data-testid={`task-video-status-${v.task_code}`}>
                  {v.status === "ready" ? "ready — visible to staff" : v.status}
                </span>
              </div>
              {v.status === "ready" ? (
                <video controls preload="metadata" src={`/api/task-videos/${v.id}/file`} className="mt-3 aspect-video w-full max-w-md rounded-lg bg-stone-900" data-testid={`task-video-player-${v.task_code}`} />
              ) : (
                <>
                  <Textarea rows={5} value={edits[v.id] ?? v.script} onChange={(e) => setEdits((s) => ({ ...s, [v.id]: e.target.value }))}
                    className="mt-2 border-stone-300 text-sm" data-testid={`task-video-script-${v.task_code}`} disabled={v.status === "rendering"} />
                  {v.error && <p className="mt-1 text-xs text-[#C0392B]" data-testid={`task-video-error-${v.task_code}`}>{v.error}</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Button size="sm" variant="outline" onClick={() => saveScript(v)} disabled={busy === v.id || v.status === "rendering"} className="border-stone-300" data-testid={`task-video-save-${v.task_code}`}>Save script</Button>
                    {v.status !== "approved" && v.status !== "rendering" && (
                      <Button size="sm" onClick={() => approve(v)} disabled={busy === v.id} className="bg-[#2D5A27] text-white hover:bg-[#23481e]" data-testid={`task-video-approve-${v.task_code}`}>Approve script</Button>
                    )}
                    {v.status === "approved" && (
                      <>
                        <select value={(pick[v.id] || {}).avatar || ""} onChange={(e) => setPick((s) => ({ ...s, [v.id]: { ...s[v.id], avatar: e.target.value } }))}
                          className="h-8 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid={`task-video-avatar-${v.task_code}`}>
                          <option value="">Avatar…</option>
                          {avatars.map((a) => <option key={a.id} value={a.id}>{a.name}{a.kind === "yours" ? " (yours)" : ""}</option>)}
                        </select>
                        <select value={(pick[v.id] || {}).voice || ""} onChange={(e) => setPick((s) => ({ ...s, [v.id]: { ...s[v.id], voice: e.target.value } }))}
                          className="h-8 rounded-md border border-stone-300 bg-white px-2 text-sm" data-testid={`task-video-voice-${v.task_code}`}>
                          <option value="">Default voice</option>
                          {voices.map((vo) => <option key={vo.voice_id} value={vo.voice_id}>{vo.name}{vo.kind === "custom" ? " (yours)" : ""}</option>)}
                        </select>
                        <Button size="sm" onClick={() => render(v)} disabled={busy === v.id} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid={`task-video-render-${v.task_code}`}>
                          <Play size={13} className="mr-1" /> Render video
                        </Button>
                      </>
                    )}
                    {v.status === "rendering" && <span className="inline-flex items-center gap-1.5 text-xs text-[#8a5d14]"><CircleNotch size={13} className="animate-spin" /> Rendering with HeyGen…</span>}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {videos && videos.length === 0 && <p className="mt-3 text-sm text-stone-500">No scripts yet — generate them to start.</p>}
    </div>
  );
}
